import { useState } from 'react';
import { Link } from 'react-router-dom';

type CardCategory = 'all' | 'beginner' | 'airline' | 'hotel' | 'travel';

interface CreditCard {
  id: number;
  name: string;
  issuer: string;
  category: CardCategory[];
  signupBonus: string;
  annualFee: string;
  bestFor: string;
  keyBenefits: string[];
  image: string;
  referralLink?: string;
}

export default function Cards() {
  const [activeCategory, setActiveCategory] = useState<CardCategory>('all');

  const cards: CreditCard[] = [
    {
      id: 1,
      name: 'Chase Sapphire Preferred',
      issuer: 'Chase',
      category: ['travel', 'beginner'],
      signupBonus: '60,000 points after $4,000 spend in 3 months',
      annualFee: '$95',
      bestFor: 'Overall travel rewards and flexibility',
      keyBenefits: [
        '2x points on travel and dining',
        '25% more value when redeeming through Chase Travel',
        'Transfer points to 14+ airline and hotel partners',
        'No foreign transaction fees'
      ],
      image: 'https://creditcards.chase.com/K-Marketplace/images/cardart/sapphire_preferred_card.png',
      referralLink: 'https://www.referyourchasecard.com/19u/A20T9II9F6'
    },
    {
      id: 2,
      name: 'Chase Sapphire Reserve',
      issuer: 'Chase',
      category: ['travel'],
      signupBonus: '60,000 points after $4,000 spend in 3 months',
      annualFee: '$550',
      bestFor: 'Premium travel benefits and luxury perks',
      keyBenefits: [
        '3x points on travel and dining',
        '50% more value when redeeming through Chase Travel',
        'Priority Pass Select lounge access',
        '$300 annual travel credit'
      ],
      image: 'https://creditcards.chase.com/K-Marketplace/images/cardart/sapphire_reserve_card.png',
      referralLink: 'https://www.referyourchasecard.com/19u/A20T9II9F6'
    },
    {
      id: 3,
      name: 'American Express Gold Card',
      issuer: 'American Express',
      category: ['travel', 'beginner'],
      signupBonus: '60,000 points after $4,000 spend in 6 months',
      annualFee: '$250',
      bestFor: 'Dining and grocery rewards',
      keyBenefits: [
        '4x points at restaurants worldwide',
        '4x points at U.S. supermarkets (up to $25,000/year)',
        '3x points on flights booked directly with airlines',
        '$120 dining credit annually'
      ],
      image: 'https://icm.aexp-static.com/Internet/Acquisition/US_en/AppContent/OneSite/category/cardarts/gold-card.png',
      referralLink: 'https://americanexpress.com/en-us/referral/gold-card?ref=COLTOW7mbC&XL=MIMNS'
    },
    {
      id: 4,
      name: 'Capital One Venture Rewards',
      issuer: 'Capital One',
      category: ['travel', 'beginner'],
      signupBonus: '75,000 miles after $4,000 spend in 3 months',
      annualFee: '$95',
      bestFor: 'Simple unlimited travel rewards',
      keyBenefits: [
        '2x miles on every purchase',
        '5x miles on hotels and rental cars through Capital One Travel',
        'Transfer miles to 15+ travel partners',
        'No foreign transaction fees'
      ],
      image: 'https://ecm.capitalone.com/WCM/card/products/venture-card-art.png'
    },
    {
      id: 5,
      name: 'American Express Platinum Card',
      issuer: 'American Express',
      category: ['travel'],
      signupBonus: '80,000 points after $6,000 spend in 6 months',
      annualFee: '$695',
      bestFor: 'Premium travel benefits and luxury perks',
      keyBenefits: [
        '5x points on flights booked directly with airlines',
        'Access to 1,400+ airport lounges worldwide',
        '$200 annual airline fee credit',
        'Automatic Gold status with Hilton and Marriott'
      ],
      image: 'https://icm.aexp-static.com/Internet/Acquisition/US_en/AppContent/OneSite/category/cardarts/platinum-card.png'
    },
    {
      id: 6,
      name: 'Delta SkyMiles Gold',
      issuer: 'American Express',
      category: ['airline', 'beginner'],
      signupBonus: '50,000 miles after $2,000 spend in 3 months',
      annualFee: '$99',
      bestFor: 'Delta Airlines loyalists',
      keyBenefits: [
        'First checked bag free on Delta flights',
        '2x miles on Delta purchases and restaurants',
        'Priority boarding',
        'Discounted Delta Sky Club access'
      ],
      image: 'https://icm.aexp-static.com/Internet/Acquisition/US_en/AppContent/OneSite/category/cardarts/delta-skymiles-gold.png',
      referralLink: 'https://americanexpress.com/en-us/referral/delta-skymiles-gold-american-express-card?ref=COLTOWbaV2&XL=MIMNS'
    },
    {
      id: 7,
      name: 'Delta SkyMiles Platinum',
      issuer: 'American Express',
      category: ['airline'],
      signupBonus: '70,000 miles after $3,000 spend in 6 months',
      annualFee: '$250',
      bestFor: 'Frequent Delta travelers wanting premium benefits',
      keyBenefits: [
        'First checked bag free for you and up to 8 companions',
        '3x miles on Delta purchases',
        'Priority boarding and Zone 1 boarding',
        'Annual companion certificate'
      ],
      image: 'https://icm.aexp-static.com/Internet/Acquisition/US_en/AppContent/OneSite/category/cardarts/delta-skymiles-platinum.png',
      referralLink: 'https://americanexpress.com/en-us/referral/delta-skymiles-platinum-american-express-card?ref=COLTOWmVm4&XL=MIMNS'
    },
    {
      id: 8,
      name: 'Delta SkyMiles Reserve',
      issuer: 'American Express',
      category: ['airline'],
      signupBonus: '80,000 miles after $5,000 spend in 6 months',
      annualFee: '$550',
      bestFor: 'Frequent Delta travelers seeking premium benefits',
      keyBenefits: [
        'Complimentary Delta Sky Club access',
        'First checked bag free for you and up to 8 companions',
        '3x miles on Delta purchases',
        'Annual companion certificate'
      ],
      image: 'https://icm.aexp-static.com/Internet/Acquisition/US_en/AppContent/OneSite/category/cardarts/delta-skymiles-reserve.png'
    },
    {
      id: 9,
      name: 'Southwest Rapid Rewards Plus',
      issuer: 'Chase',
      category: ['airline'],
      signupBonus: '50,000 points after $1,000 spend in 3 months',
      annualFee: '$99',
      bestFor: 'Southwest Airlines travelers',
      keyBenefits: [
        '2x points on Southwest purchases',
        '2x points on local transit and commuting',
        'Earn toward Companion Pass',
        '4 upgraded boardings per year'
      ],
      image: 'https://creditcards.chase.com/K-Marketplace/images/cardart/southwest_plus_card.png'
    },
    {
      id: 10,
      name: 'Marriott Bonvoy Boundless',
      issuer: 'Chase',
      category: ['hotel'],
      signupBonus: '75,000 points after $3,000 spend in 3 months',
      annualFee: '$95',
      bestFor: 'Marriott hotel stays',
      keyBenefits: [
        'Free night award annually (up to 50,000 points)',
        '6x points at Marriott properties',
        'Automatic Silver Elite status',
        '2x points on all other purchases'
      ],
      image: 'https://creditcards.chase.com/K-Marketplace/images/cardart/marriott_bonvoy_boundless_card.png'
    },
    {
      id: 11,
      name: 'Hilton Honors American Express',
      issuer: 'American Express',
      category: ['hotel'],
      signupBonus: '100,000 points after $2,000 spend in 6 months',
      annualFee: '$0',
      bestFor: 'Hilton hotel stays with no annual fee',
      keyBenefits: [
        '7x points at Hilton properties',
        '5x points at restaurants, supermarkets, and gas stations',
        'Automatic Silver status',
        'No foreign transaction fees'
      ],
      image: 'https://icm.aexp-static.com/Internet/Acquisition/US_en/AppContent/OneSite/category/cardarts/hilton-honors-card.png'
    }
  ];

  const filteredCards = activeCategory === 'all' 
    ? cards 
    : cards.filter(card => card.category.includes(activeCategory));

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <h1 className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                Points Made Simple
              </h1>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
              <Link to="/#how-it-works" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">How It Works</Link>
              <Link to="/cards" className="text-teal-600 font-semibold cursor-pointer whitespace-nowrap">Card Recommendations</Link>
              <Link to="/quiz" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Find the Best Card</Link>
              <Link to="/#about" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">About Me</Link>
              <Link to="/#contact" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Contact</Link>
            </div>
            <button className="md:hidden w-8 h-8 flex items-center justify-center">
              <i className="ri-menu-line text-2xl text-gray-700"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Credit Card Recommendations</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The best cards for beginners to start earning points and unlock free travel. Each recommendation is based on real experience and proven results.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white sticky top-16 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === 'all'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Cards
            </button>
            <button
              onClick={() => setActiveCategory('beginner')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === 'beginner'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-star-line mr-2"></i>
              Best Beginner Cards
            </button>
            <button
              onClick={() => setActiveCategory('travel')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === 'travel'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-flight-takeoff-line mr-2"></i>
              Overall Travel Cards
            </button>
            <button
              onClick={() => setActiveCategory('airline')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === 'airline'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-plane-line mr-2"></i>
              Airline Cards
            </button>
            <button
              onClick={() => setActiveCategory('hotel')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === 'hotel'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-hotel-line mr-2"></i>
              Hotel Cards
            </button>
          </div>
        </div>
      </section>

      {/* Cards Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredCards.map((card) => (
              <div key={card.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden border border-gray-100">
                <div className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{card.name}</h3>
                      <p className="text-gray-600">{card.issuer}</p>
                    </div>
                    <div className="w-24 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center ml-4">
                      <img src={card.image} alt={card.name} className="w-full h-full object-cover object-top rounded-lg" />
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="bg-teal-50 p-4 rounded-lg">
                      <div className="text-sm font-semibold text-teal-900 mb-1">Sign-up Bonus</div>
                      <div className="text-teal-700">{card.signupBonus}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-1">Annual Fee</div>
                        <div className="text-lg font-bold text-gray-900">{card.annualFee}</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-1">Best For</div>
                        <div className="text-sm text-gray-600">{card.bestFor}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Benefits</h4>
                    <ul className="space-y-2">
                      {card.keyBenefits.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-check-line text-teal-600 mt-1 mr-2 flex-shrink-0"></i>
                          <span className="text-gray-700 text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {card.referralLink ? (
                    <a 
                      href={card.referralLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer block text-center"
                    >
                      Apply Now
                    </a>
                  ) : (
                    <button className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                      Learn More
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Tips for Choosing Your First Card</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Here's what I wish I knew when I started
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Start with One Card</h4>
              <p className="text-gray-600">
                Don't overwhelm yourself. Pick one card that matches your spending habits and master it before adding more.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Meet the Minimum Spend</h4>
              <p className="text-gray-600">
                Only apply if you can naturally meet the spending requirement. Never overspend just for points.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Pay in Full Always</h4>
              <p className="text-gray-600">
                Interest charges will erase any points value. Always pay your balance in full every month.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-star-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Focus on Sign-up Bonuses</h4>
              <p className="text-gray-600">
                The biggest value comes from sign-up bonuses. They can be worth $500-$1,000+ in travel.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Have a Goal in Mind</h4>
              <p className="text-gray-600">
                Know where you want to go. It makes choosing cards and redeeming points much easier.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Be Patient</h4>
              <p className="text-gray-600">
                Building points takes time. Start now, stay consistent, and you'll be traveling for free before you know it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-start">
              <i className="ri-information-line text-teal-600 text-xl mt-1 mr-3 flex-shrink-0"></i>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Disclosure</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  This website may contain affiliate links and I may be compensated if you make a purchase after clicking on my referral links above. This compensation helps support the content and recommendations I provide, but it does not influence my opinions or recommendations. I only recommend credit cards that I have personally used or thoroughly researched and believe will provide value to my readers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">Questions About Which Card to Choose?</h3>
          <p className="text-xl text-teal-50 mb-8">
            I'm here to help you find the perfect card for your travel goals.
          </p>
          <Link to="/#contact" className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
            Get In Touch
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>Points Made Simple</h4>
              <p className="text-gray-400">
                Helping beginners unlock free travel through credit card points and miles.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/#how-it-works" className="text-gray-400 hover:text-white transition-colors cursor-pointer">How It Works</Link></li>
                <li><Link to="/cards" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Card Recommendations</Link></li>
                <li><Link to="/#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Me</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Connect</h5>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Points Made Simple. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
